import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Card from "@mui/material/Card";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import BasicLayout from "layouts/authentication/components/BasicLayout";

import bgImage from "assets/images/home.jpg";

import { getAuthToken, setAuthToken } from "utils/authToken";
import apiClient from "utils/apiClient";
import { ENDPOINTS } from "utils/endpoints";

function SignIn() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [snack, setSnack] = useState({ open: false, content: "" });
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAuthToken();
    if (token) navigate("/dashboard");
  }, [navigate]);

  const validate = () => {
    const e = {};
    if (!username.trim()) e.username = "Required";
    if (!password) e.password = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const formData = new FormData();
    formData.append("username", username);
    formData.append("password", password);

    try {
      const res = await apiClient.post(ENDPOINTS.auth.login, formData);
      setAuthToken(res.data.access_token);
      // Onboarding is the only place allergies and motivations are collected,
      // and it used to be reachable from exactly one redirect — the one right
      // after sign-up. Anyone who closed the tab partway through never saw it
      // again. Sign-in asks the server whether they finished, so an unfinished
      // one is resumed instead of silently skipped forever. If the check
      // itself fails, go to the dashboard: a working app beats a form.
      let needsOnboarding = false;
      try {
        // Admins have no onboarding to finish and belong on /admin, which App
        // routes them to — asking would strand them on a form built for cooks.
        if (jwtDecode(res.data.access_token).role !== "admin") {
          const status = await apiClient.get(ENDPOINTS.onboarding.status);
          needsOnboarding = status.data?.completed === false;
        }
      } catch {
        needsOnboarding = false;
      }
      navigate(needsOnboarding ? "/onboarding" : "/dashboard", { replace: true });
    } catch (err) {
      setSnack({ open: true, content: err.response?.data?.detail || "Login failed." });
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox pt={4} pb={3} px={3}>
          <MDTypography variant="h4" fontWeight="medium" textAlign="center" mb={3}>
            Sign in
          </MDTypography>

          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Email or Username"
                fullWidth
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                error={!!errors.username}
                helperText={errors.username}
              />
            </MDBox>

            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!errors.password}
                helperText={errors.password}
              />
            </MDBox>

            <MDBox display="flex" justifyContent="flex-end" mb={2}>
              <MDTypography
                component={Link}
                to="/authentication/reset-password"
                variant="button"
                color="info"
                fontWeight="regular"
                sx={{ cursor: "pointer" }}
              >
                Forgot password?
              </MDTypography>
            </MDBox>

            <MDBox mt={4} mb={1}>
              <MDButton type="submit" variant="gradient" color="info" fullWidth>
                Sign in
              </MDButton>
            </MDBox>

            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Don&apos;t have an account?{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-up"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Sign up
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      <MDSnackbar
        color="error"
        icon="warning"
        title="Sign in failed"
        dateTime=""
        content={snack.content}
        open={snack.open}
        close={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </BasicLayout>
  );
}

export default SignIn;
